/**
 * useRealtimeDashboard Hook
 *
 * Subscribes to Supabase Realtime for dashboard-related tables
 * and triggers React Query refetch when data changes.
 * Handles multiple tables: referrals, connections, user_profiles,
 * availability_requests, and availability_request_recipients.
 *
 * Uses the base useRealtimeSubscription hook for:
 * - Automatic reconnection with exponential backoff
 * - Connection status tracking
 * - Error handling with Sentry integration
 * - Debounced query invalidation
 * - Immediate refetch for time-sensitive data (availability requests)
 */

import { useCallback, useMemo, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { RealtimeChannel } from '@supabase/supabase-js';
import { useRealtimeSubscription, RealtimeStatus } from './useRealtimeSubscription';

interface UseRealtimeDashboardOptions {
  userId?: string;
  enabled?: boolean;
  onUpdate?: (table: string, payload: unknown) => void;
  onStatusChange?: (status: RealtimeStatus) => void;
}

interface UseRealtimeDashboardResult {
  /** Current connection status */
  status: RealtimeStatus;
  /** Error if status is 'error' */
  error: Error | null;
  /** Manually unsubscribe */
  unsubscribe: () => void;
  /** Manually retry connection */
  retry: () => void;
  /** Number of reconnection attempts */
  reconnectAttempts: number;
}

export function useRealtimeDashboard(
  options: UseRealtimeDashboardOptions = {}
): UseRealtimeDashboardResult {
  const { userId, enabled = true, onUpdate, onStatusChange } = options;
  const queryClient = useQueryClient();

  // Memoize the change handler to prevent re-subscriptions
  const handlePostgresChange = useCallback(
    (table: string, payload: { eventType: string; new?: Record<string, unknown>; old?: Record<string, unknown> }) => {
      const data = payload.new || payload.old;

      // Handle each table type differently
      switch (table) {
        case 'referrals': {
          const referralData = data as { sender_id?: string; receiver_id?: string } | undefined;
          // Only process if this change affects the current user
          if (referralData?.sender_id !== userId && referralData?.receiver_id !== userId) {
            return;
          }
          // Invalidate dashboard stats, referrals, and needs-attention queries
          queryClient.invalidateQueries({ queryKey: ['/api/workflows/dashboard'] });
          queryClient.invalidateQueries({ queryKey: ['/api/workflows/referral/list'] });
          queryClient.invalidateQueries({ queryKey: ['/api/workflows/needs-attention'] });
          break;
        }

        case 'connections': {
          const connectionData = data as { user_id?: string; connected_user_id?: string } | undefined;
          // Only process if this change affects the current user
          if (connectionData?.user_id !== userId && connectionData?.connected_user_id !== userId) {
            return;
          }
          // Invalidate dashboard stats query (for partner connections count)
          queryClient.invalidateQueries({ queryKey: ['/api/workflows/dashboard'] });
          break;
        }

        case 'user_profiles': {
          // For user_profiles, we filter by user ID in the subscription
          // Invalidate dashboard stats query (for subscription plan and goal)
          queryClient.invalidateQueries({ queryKey: ['/api/workflows/dashboard'] });
          break;
        }

        case 'availability_requests': {
          const availabilityData = data as { sender_id?: string; id?: string } | undefined;
          console.log('[useRealtimeDashboard] availability_requests event:', {
            eventType: payload.eventType,
            senderId: availabilityData?.sender_id,
            requestId: availabilityData?.id,
            currentUserId: userId
          });

          // Process if this is the current user's request (sender view)
          if (availabilityData?.sender_id === userId) {
            console.log('[useRealtimeDashboard] Refetching sent requests (user is sender)');
            queryClient.invalidateQueries({ queryKey: ['availability-requests'] });
            queryClient.invalidateQueries({ queryKey: ['availability-requests', 'with-responses'] });
            queryClient.refetchQueries({ queryKey: ['availability-requests', 'with-responses'] });
          }

          // ALSO refetch received requests - the API will filter for recipient_id
          // This catches new availability requests sent TO this user
          console.log('[useRealtimeDashboard] Refetching received requests (in case user is recipient)');
          queryClient.invalidateQueries({ queryKey: ['availability-requests', 'received'] });
          queryClient.refetchQueries({ queryKey: ['availability-requests', 'received'] });
          // Refresh needs-attention card (availability requests appear there)
          queryClient.invalidateQueries({ queryKey: ['/api/workflows/needs-attention'] });
          break;
        }

        case 'availability_request_recipients': {
          // CRITICAL: RLS filtering may prevent INSERT events from being delivered
          // As a workaround, we refetch on ANY event (INSERT/UPDATE/DELETE)
          // The backend API will handle proper filtering by recipient_id

          // For DELETE events, recipient_id might only be in payload.old
          // For INSERT/UPDATE events, it's in payload.new
          const newData = payload.new as { recipient_id?: string; availability_request_id?: string } | undefined;
          const oldData = payload.old as { recipient_id?: string; availability_request_id?: string } | undefined;
          const recipientId = newData?.recipient_id || oldData?.recipient_id;

          console.log('[useRealtimeDashboard] availability_request_recipients event:', {
            eventType: payload.eventType,
            recipientId,
            currentUserId: userId,
            matches: recipientId === userId,
            hasNew: !!newData?.recipient_id,
            hasOld: !!oldData?.recipient_id,
            note: 'Refetching regardless of match due to RLS INSERT event filtering'
          });

          // WORKAROUND: Since INSERT events may not be delivered due to RLS filtering,
          // we refetch on ANY change to this table. The backend will filter properly.
          console.log('[useRealtimeDashboard] Refetching received requests (all events)');
          queryClient.invalidateQueries({ queryKey: ['availability-requests', 'received'] });
          queryClient.refetchQueries({ queryKey: ['availability-requests', 'received'] });

          // Always invalidate the sender's view when recipients respond.
          // Two consumers exist: the legacy 'with-responses' card and the
          // current AvailabilityRequestsPanel which uses ['..., 'sent', 'list'].
          queryClient.invalidateQueries({ queryKey: ['availability-requests', 'with-responses'] });
          queryClient.invalidateQueries({ queryKey: ['availability-requests', 'sent', 'list'] });
          queryClient.refetchQueries({ queryKey: ['availability-requests', 'with-responses'] });
          queryClient.refetchQueries({ queryKey: ['availability-requests', 'sent', 'list'] });
          // Refresh needs-attention card
          queryClient.invalidateQueries({ queryKey: ['/api/workflows/needs-attention'] });
          break;
        }
      }

      // Call optional callback
      onUpdate?.(table, payload);
    },
    [userId, queryClient, onUpdate]
  );

  // Memoize tables configuration
  // Subscribe to multiple tables for dashboard updates
  const tables = useMemo(
    () => [
      {
        table: 'referrals',
        event: '*' as const,
        // No filter - we filter client-side for OR condition (sender_id OR receiver_id)
      },
      {
        table: 'connections',
        event: '*' as const,
        // No filter - we filter client-side for OR condition (user_id OR connected_user_id)
      },
      {
        table: 'user_profiles',
        event: 'UPDATE' as const,
        filter: userId ? `id=eq.${userId}` : undefined,
      },
      {
        table: 'availability_requests',
        event: '*' as const,
        // No filter - we filter client-side for sender_id
      },
      {
        table: 'availability_request_recipients',
        event: '*' as const,
        // No filter - we filter client-side for recipient_id and updates
      },
    ],
    [userId]
  );

  // Use the base subscription hook
  const subscription = useRealtimeSubscription({
    channelName: `dashboard-${userId}`,
    enabled: enabled && !!userId,
    tables,
    onPostgresChange: handlePostgresChange,
    onStatusChange,
    debounceMs: 500, // Debounce rapid updates
  });

  // Additional listener for realtime.messages table INSERT events
  // This detects broadcast messages inserted by database triggers
  // This bypasses RLS filtering that prevents INSERT events from reaching recipients
  const messagesChannelRef = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    if (!enabled || !userId) {
      return;
    }

    // Fetch secure broadcast channel name from backend
    // The channel includes an HMAC signature to prevent guessing
    const setupSecureBroadcast = async () => {
      try {
        const response = await fetch('/api/workflows/realtime-channel/secure-channel', {
          method: 'GET',
          credentials: 'include',
        });

        if (!response.ok) {
          console.error('[useRealtimeDashboard] Failed to fetch secure channel:', response.status);
          return;
        }

        const { channelName } = await response.json();
        console.log('[useRealtimeDashboard] Subscribing to secure broadcast channel:', channelName);

        const channel = supabase.channel(channelName, {
          config: {
            broadcast: { self: false }
          }
        });

        // Add catch-all logging for ALL broadcast events (for debugging)
        channel.on('broadcast', {}, (payload) => {
          console.log('[useRealtimeDashboard] 🔍 RAW broadcast received:', {
            event: payload.event,
            type: payload.type,
            payload: payload.payload,
            channelName,
            timestamp: new Date().toISOString()
          });
        });

        // Specific event handlers
        channel
          .on(
            'broadcast',
            { event: 'availability_request_recipient_inserted' },
            (payload) => {
              console.log('[useRealtimeDashboard] Received INSERT broadcast event:', payload);
              queryClient.invalidateQueries({ queryKey: ['availability-requests', 'received'] });
              queryClient.refetchQueries({ queryKey: ['availability-requests', 'received'] });
              // Sender's panel must also reflect the new recipient row.
              queryClient.invalidateQueries({ queryKey: ['availability-requests', 'sent', 'list'] });
              queryClient.refetchQueries({ queryKey: ['availability-requests', 'sent', 'list'] });
            }
          )
          .on(
            'broadcast',
            { event: 'availability_request_recipient_updated' },
            (payload) => {
              console.log('[useRealtimeDashboard] Received UPDATE broadcast event:', payload);
              queryClient.invalidateQueries({ queryKey: ['availability-requests', 'received'] });
              queryClient.refetchQueries({ queryKey: ['availability-requests', 'received'] });

              // Also refetch sender's views (legacy + current panel) to see updated response status.
              queryClient.invalidateQueries({ queryKey: ['availability-requests', 'with-responses'] });
              queryClient.refetchQueries({ queryKey: ['availability-requests', 'with-responses'] });
              queryClient.invalidateQueries({ queryKey: ['availability-requests', 'sent', 'list'] });
              queryClient.refetchQueries({ queryKey: ['availability-requests', 'sent', 'list'] });
            }
          )
          .on(
            'broadcast',
            { event: 'availability_request_recipient_deleted' },
            (payload) => {
              console.log('[useRealtimeDashboard] Received DELETE broadcast event:', payload);
              queryClient.invalidateQueries({ queryKey: ['availability-requests', 'received'] });
              queryClient.refetchQueries({ queryKey: ['availability-requests', 'received'] });
              queryClient.invalidateQueries({ queryKey: ['availability-requests', 'sent', 'list'] });
              queryClient.refetchQueries({ queryKey: ['availability-requests', 'sent', 'list'] });
            }
          )
          .on(
            'broadcast',
            { event: 'availability_request_status_updated' },
            (payload) => {
              console.log('[useRealtimeDashboard] ✅ Availability request STATUS UPDATED:', payload);

              // Handle potential nested payload structure
              const eventData = payload.payload || payload;

              console.log('[useRealtimeDashboard] Event data:', {
                availability_request_id: eventData.availability_request_id,
                old_status: eventData.old_status,
                new_status: eventData.new_status
              });

              // Invalidate received requests cache (backend filters by status='active')
              // When status changes to 'converted', the request will be excluded on refetch
              console.log('[useRealtimeDashboard] Invalidating received requests cache');
              queryClient.invalidateQueries({
                queryKey: ['availability-requests', 'received'],
                exact: false
              });
            }
          )
          .subscribe((status) => {
            console.log('[useRealtimeDashboard] 📡 Broadcast channel subscription status:', status);
            if (status === 'SUBSCRIBED') {
              console.log('[useRealtimeDashboard] ✅ Successfully subscribed to channel:', channelName);
            } else if (status === 'CLOSED') {
              console.log('[useRealtimeDashboard] ⚠️ Channel closed:', channelName);
            } else if (status === 'CHANNEL_ERROR') {
              console.error('[useRealtimeDashboard] ❌ Channel error:', channelName);
            }
          });

        messagesChannelRef.current = channel;
      } catch (error) {
        console.error('[useRealtimeDashboard] Error setting up secure broadcast:', error);
      }
    };

    setupSecureBroadcast();

    // Cleanup on unmount
    return () => {
      console.log('[useRealtimeDashboard] Cleaning up messages channel');
      if (messagesChannelRef.current) {
        const channelToRemove = messagesChannelRef.current;
        messagesChannelRef.current = null;
        supabase.removeChannel(channelToRemove);
      }
    };
  }, [enabled, userId, queryClient]);

  // Diagnostic: Log subscription state
  console.log('[useRealtimeDashboard] Subscription state:', {
    enabled,
    userId,
    subscriptionEnabled: enabled && !!userId,
    status: subscription.status
  });

  return {
    status: subscription.status,
    error: subscription.error,
    unsubscribe: subscription.unsubscribe,
    retry: subscription.retry,
    reconnectAttempts: subscription.reconnectAttempts,
  };
}

export default useRealtimeDashboard;
